import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
  Textarea,
  Switch,
  Label,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@wove/web-ui'
import { ConditionBuilder } from './ConditionBuilder'
import { RequirementsEditor } from './RequirementsEditor'
import { RuleTester } from './RuleTester'
import {
  createValidationRule,
  updateValidationRule,
  ValidationRequirement,
  type ValidationRule,
} from '@wove/api'
import { toast } from 'sonner'

interface ValidationRuleFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  rule?: ValidationRule | null
  onSuccess: () => void
}

export function ValidationRuleFormDialog({
  open,
  onOpenChange,
  rule,
  onSuccess,
}: ValidationRuleFormDialogProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    conditions: { operator: 'AND' as const, conditions: [] } as any,
    requirements: [] as any[],
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [activeTab, setActiveTab] = useState('basic')
  const [isTesterOpen, setIsTesterOpen] = useState(false)

  useEffect(() => {
    if (rule) {
      setFormData({
        name: rule.name,
        description: rule.description || '',
        is_active: rule.is_active,
        conditions: rule.conditions || { operator: 'AND', conditions: [] },
        requirements: rule.requirements || [],
      })
    } else {
      setFormData({
        name: '',
        description: '',
        is_active: true,
        conditions: { operator: 'AND', conditions: [] },
        requirements: [],
      })
    }
  }, [rule])

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Please provide a rule name')
      return
    }

    if (formData.conditions.conditions.length === 0) {
      toast.error('Please add at least one condition')
      setActiveTab('conditions')
      return
    }

    if (formData.requirements.length === 0) {
      toast.error('Please add at least one requirement')
      setActiveTab('requirements')
      return
    }

    setIsSubmitting(true)

    try {
      if (rule) {
        await updateValidationRule(rule.id, formData)
        toast.success('Validation rule updated successfully')
      } else {
        await createValidationRule(formData)
        toast.success('Validation rule created successfully')
      }
      onSuccess()
    } catch (error) {
      toast.error('Failed to save validation rule')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleTestRule = () => {
    setIsTesterOpen(true)
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle>{rule ? 'Edit Validation Rule' : 'Create Validation Rule'}</DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6">
            <Tabs
              value={activeTab}
              onValueChange={setActiveTab}
              className="mt-6"
              defaultValue="basic"
            >
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="basic">Basic Info</TabsTrigger>
                <TabsTrigger value="conditions">Conditions</TabsTrigger>
                <TabsTrigger value="requirements">Requirements</TabsTrigger>
              </TabsList>

              <TabsContent value="basic" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Rule Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Air Freight Requirements"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe when this rule applies and what it does"
                    rows={3}
                  />
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active">Active</Label>
                </div>
              </TabsContent>

              <TabsContent value="conditions" className="mt-4">
                <div className="space-y-2 mb-4">
                  <Label>When to Apply This Rule</Label>
                  <p className="text-sm text-gray-600">
                    Define conditions that determine when this validation rule should be applied
                  </p>
                </div>
                <ConditionBuilder
                  conditions={formData.conditions}
                  onChange={(conditions) => setFormData({ ...formData, conditions })}
                />
              </TabsContent>

              <TabsContent value="requirements" className="mt-4 pb-6">
                <div className="space-y-2 mb-4">
                  <Label>Field Requirements</Label>
                  <p className="text-sm text-gray-600">
                    Specify which fields become required, optional, or hidden when this rule applies
                  </p>
                </div>
                <RequirementsEditor
                  requirements={formData.requirements}
                  onChange={(requirements) => setFormData({ ...formData, requirements })}
                />
              </TabsContent>
            </Tabs>
          </div>

          <div className="border-t px-6 py-4 bg-gray-50 flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleTestRule}
              disabled={isSubmitting || formData.conditions.conditions.length === 0}
            >
              Test Rule
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : rule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <RuleTester
        open={isTesterOpen}
        onOpenChange={setIsTesterOpen}
        rule={{
          conditions: formData.conditions,
          requirements: formData.requirements,
        }}
      />
    </>
  )
}
